import React from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Typography, Box, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import { DialogHeader } from './DialogHeader';
import type { SageDriftDto } from '../../types/sage';

interface SageDriftDialogProps {
  open: boolean;
  /** The drift result to display. Null renders an empty shell (guarded by `open`). */
  drift: SageDriftDto | null;
  /** Lowercase document noun, e.g. 'invoice', 'purchase order', 'credit note'. */
  documentLabel: string;
  /** Disables the actions and keeps the dialog open while reconcile/generate runs. */
  busy: boolean;
  onUseDris: () => void;
  onUseSage: () => void;
  onGenerateAnyway: () => void;
  onCancel: () => void;
}

/**
 * Interrupts document generation when the billed party's DRIS details have drifted
 * from its linked Sage record. Lets the user choose which source of truth to use
 * (or generate anyway) before the document is created.
 *
 * Fully controlled: `open` and `busy` are owned by the caller ({@link useSageDriftGuard}).
 * The action buttons only report the choice — the caller runs the async work, shows
 * `busy` across it, then closes the dialog.
 */
export const SageDriftDialog: React.FC<SageDriftDialogProps> = ({
  open,
  drift,
  documentLabel,
  busy,
  onUseDris,
  onUseSage,
  onGenerateAnyway,
  onCancel,
}) => {
  const linkKind = (drift?.linkKind ?? 'Customer').toLowerCase();
  const entityName = drift?.entityName ?? '';
  const fields = drift?.fields ?? [];

  return (
    <Dialog open={open} onClose={busy ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogHeader
        title="Details differ from Sage"
        icon={<SyncProblemIcon color="warning" />}
        onClose={onCancel}
        disabled={busy}
      />

      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The {linkKind} <strong>{entityName}</strong> has details that differ between DRIS
          and Sage. Choose which values to use before generating the {documentLabel}.
        </Typography>

        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Field</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>In DRIS</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>In Sage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((f) => (
                <TableRow key={f.field}>
                  <TableCell>{f.field}</TableCell>
                  <TableCell>{f.drisValue || '-'}</TableCell>
                  <TableCell>{f.sageValue || '-'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {busy && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">Applying changes...</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onCancel} color="inherit" disabled={busy}>Cancel</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onGenerateAnyway} disabled={busy}>Generate anyway</Button>
        <Button onClick={onUseSage} disabled={busy} variant="outlined">Use Sage values</Button>
        <Button onClick={onUseDris} disabled={busy} variant="contained">Use DRIS values</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SageDriftDialog;
